import { useCandidateAuthStore } from "@repo/auth";
import { Button, Card, Progress, Textarea } from "@repo/ui";
import { useAction, useMutation, useQuery } from "convex/react";
import { ChevronDown, Loader2, Mic, MicOff, Send } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../../../convex/_generated/api";
import type { Id } from "../../../../../convex/_generated/dataModel";
import { StatutGlobalEnum, estEvaluationSoumise } from "../../../../../convex/schema";
import { useAudioRecorder } from "../../hooks/useAudioRecorder";
import { useCheatingDetection } from "../../hooks/useCheatingDetection";
import type { EntStructEvaluation, MessageRole } from "../../lib/types";
import { isBackendError } from "../../lib/types";
import { calculerProgressionQuestions } from "../../lib/utils";
import { useChatStore } from "../../stores/chatStore";
import { ChatBubble } from "./ChatBubble";
import { CompletionModal } from "./CompletionModal";
import { IntroModal } from "./IntroModal";
import { RecordingIndicator } from "./RecordingIndicator";
import { TypingIndicator } from "./TypingIndicator";

interface Message {
  role: MessageRole;
  content: string;
}

interface ChatInterfaceProps {
  evaluation: EntStructEvaluation;
  token: string;
}

export function ChatInterface({ evaluation, token }: ChatInterfaceProps) {
  // Get parcoursId from auth store (set when coming from candidate-portail)
  const parcoursIdFromStore = useCandidateAuthStore(
    (state) => state.parcoursId,
  );

  // Query to get parcoursId from evaluationId if not in store
  // Only skip if parcoursIdFromStore has a truthy value (non-empty string)
  const parcoursFromEval = useQuery(
    api.parcoursEvaluations.obtenirParcoursIdDepuisEvaluation,
    parcoursIdFromStore ? "skip" : { evaluationId: evaluation._id },
  );

  // Use parcoursId from store or from query (prefer store if available)
  // Note: parcoursFromEval is undefined while loading, null if not found
  const parcoursId = parcoursIdFromStore || parcoursFromEval?.parcoursId;

  const {
    isTyping,
    setIsTyping,
    currentInput,
    setCurrentInput,
    addToCurrentInput,
    isSubmitting,
    setIsSubmitting,
    showCompletionModal,
    setShowCompletionModal,
    isRecording,
    setIsRecording,
    isTranscribing,
    setIsTranscribing,
    transcribedLength,
    addTranscribedLength,
    resetTranscribedLength,
  } = useChatStore();

  const {
    onQuestionAffichee,
    onKeystroke,
    onPaste,
    getCheatingMeta,
    reset: resetCheatingMeta,
  } = useCheatingDetection();

  const [isInterviewFinished, setIsInterviewFinished] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [audioVolume, setAudioVolume] = useState(0);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
    null,
  );

  // Intro modal state - show only if evaluation has not started
  const [showIntroModal, setShowIntroModal] = useState(
    evaluation.statut === StatutGlobalEnum.NON_COMMENCE,
  );
  const [isStartingInterview, setIsStartingInterview] = useState(false);

  // Audio TTS : état par index de message
  const [audioStates, setAudioStates] = useState<
    Map<
      number,
      {
        status: "idle" | "loading" | "playing" | "ready";
        blobUrl?: string;
        audio?: HTMLAudioElement;
      }
    >
  >(new Map());

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef<number>(0);
  const userScrolledUpRef = useRef<boolean>(false);
  const questionDisplayedAtRef = useRef<number | null>(null);
  const typewriterEndAtRef = useRef<number | null>(null);
  const firstResponseAtRef = useRef<number | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [isMobile] = useState(() => window.matchMedia("(hover: none) and (pointer: coarse)").matches);

  // Determine if read-only mode
  const isReadOnly = estEvaluationSoumise(evaluation.statut);

  // Récupérer le nombre total de questions du template
  const totalQuestionsTemplate = useQuery(
    api.entStruct.getNombreQuestionsTemplate,
    evaluation.donnees.template
      ? { templateSlug: evaluation.donnees.template }
      : "skip",
  );

  // Récupérer l'URL du portail candidat
  const candidatePortailUrl = useQuery(
    api.entStruct.getCandidatePortailUrl,
    {},
  );

  // Calculate progression
  const progression = useMemo(
    () =>
      calculerProgressionQuestions(
        evaluation.donnees.questionsEtat,
        evaluation.donnees.questionCouranteId,
        totalQuestionsTemplate ?? undefined,
      ),
    [
      evaluation.donnees.questionsEtat,
      evaluation.donnees.questionCouranteId,
      totalQuestionsTemplate,
    ],
  );

  const envoyerMessage = useAction(api.entStruct.envoyerMessageCandidat);
  const terminerEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const demarrerEvaluation = useMutation(api.evaluations.demarrerEvaluation);
  const initialiserEntretien = useAction(api.entStruct.initialiserEntretien);
  const transcrireAudio = useAction(api.transcription.transcrireAudio);
  const vocaliserTexte = useAction(api.tts.vocaliserTexte);

  // Durée max
  const maxDuration = Number(
    process.env.BUN_PUBLIC_MAX_RECORDING_DURATION_MINUTES || 5,
  );

  // Timeout silence
  const silenceTimeout = Number(
    process.env.BUN_PUBLIC_SILENCE_TIMEOUT_SECONDS || 30,
  );

  const isTtsEnabled = process.env.BUN_PUBLIC_TTS_ENABLED === "true";

  // Parse messages from evaluation.donnees.reponses
  const messages: Message[] = Object.entries(evaluation.donnees.reponses)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([_, msg]) => ({
      role: msg.role as MessageRole,
      content: (msg.content as string).replace(" ENT_FINISHED", "").trim(),
    }));

  // Check if interview is finished
  const lastMessageEntry = Object.entries(evaluation.donnees.reponses)
    .sort(([a], [b]) => Number(a) - Number(b))
    .pop();
  const lastMessage = lastMessageEntry ? lastMessageEntry[1] : null;
  const isFinished =
    lastMessage && typeof lastMessage.content === "string"
      ? lastMessage.content.includes("ENT_FINISHED")
      : false;

  // Check if user is near bottom (within 100px threshold)
  const checkIfNearBottom = useCallback(() => {
    if (!scrollContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 100;
  }, []);

  // Handle scroll events to track user position
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollTop } = scrollContainerRef.current;
    const nearBottom = checkIfNearBottom();

    // Detect if user scrolled up (manual scrolling)
    const scrolledUp = scrollTop < lastScrollTopRef.current - 10;
    lastScrollTopRef.current = scrollTop;

    if (scrolledUp && !nearBottom) {
      userScrolledUpRef.current = true;
    } else if (nearBottom) {
      userScrolledUpRef.current = false;
    }

    setIsNearBottom(nearBottom);
  }, [checkIfNearBottom]);

  // Auto-scroll while typewriter grows the bubble (MutationObserver on DOM changes)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const observer = new MutationObserver(() => {
      if (!userScrolledUpRef.current) {
        const isMobile = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
        messagesEndRef.current?.scrollIntoView({ behavior: isMobile ? "instant" : "smooth" });
      }
    });
    observer.observe(container, { subtree: true, characterData: true, childList: true });
    return () => observer.disconnect();
  }, []);

  // Auto-scroll to bottom when new messages arrive (only if user is near bottom and hasn't scrolled up)
  useEffect(() => {
    if (isNearBottom && !userScrolledUpRef.current) {
      const isMobile = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
      messagesEndRef.current?.scrollIntoView({ behavior: isMobile ? "instant" : "smooth" });
    }
  }, [messages.length, pendingMessage, isNearBottom]);

  const scrollToBottom = useCallback(() => {
    const isMobile = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    messagesEndRef.current?.scrollIntoView({ behavior: isMobile ? "instant" : "smooth" });
    setIsNearBottom(true);
    userScrolledUpRef.current = false;
  }, []);

  // Update finished state when detected
  useEffect(() => {
    if (isFinished) {
      setIsInterviewFinished(true);
    }
  }, [isFinished]);

  // Récupération automatique si EN_COURS mais sans thread (cas batch déjà "démarré" avant le fix)
  useEffect(() => {
    if (
      !isReadOnly &&
      !showIntroModal &&
      messages.length === 0 &&
      !evaluation.donnees.threadId &&
      parcoursId
    ) {
      initialiserEntretien({
        evaluationId: evaluation._id,
        parcoursId: parcoursId as Id<"parcoursEvaluations">,
        token,
      }).catch((err) => console.error("Auto-init entretien échoué:", err));
    }
  // Se déclenche quand parcoursId devient disponible (peut être undefined au montage)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcoursId]);

  // Track when a new question is displayed (assistant message)
  const lastMessageCountRef = useRef(-1);
  useEffect(() => {
    if (
      messages.length > 0 &&
      messages[messages.length - 1]?.role === "assistant" &&
      messages.length > lastMessageCountRef.current
    ) {
      questionDisplayedAtRef.current = Date.now();
      typewriterEndAtRef.current = null;
      firstResponseAtRef.current = null;
      onQuestionAffichee();
    }
    lastMessageCountRef.current = messages.length;
  }, [messages, onQuestionAffichee]);

  // Auto-focus textarea after chatbot responds
  useEffect(() => {
    if (!isTyping && !isSubmitting && !isReadOnly && !isInterviewFinished) {
      // Ne pas auto-focus sur mobile — ouvre le clavier et provoque un scroll parasite
      const isMobile = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
      if (!isMobile) textareaRef.current?.focus();
    }
  }, [isTyping, isSubmitting, isReadOnly, isInterviewFinished]);

  // Handle transcription of audio blob
  const handleTranscript = useCallback(
    async (blob: Blob, mimeType: string) => {
      setIsTranscribing(true);
      setIsRecording(false);
      // Note: on ne réinitialise pas audioError ici pour préserver le message de silence détecté
      try {
        // Convert blob to base64
        const arrayBuffer = await blob.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = "";
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i] as number);
        }
        const audioBase64 = btoa(binary);

        const result = await transcrireAudio({ audioBase64, mimeType });
        if (result.transcript) {
          const transcriptLength = result.transcript.trim().length;
          addToCurrentInput(result.transcript);
          // Tracker le nombre de caractères transcrits
          addTranscribedLength(transcriptLength);
        }
      } catch {
        setAudioError("La transcription a échoué. Veuillez réessayer.");
      } finally {
        setIsTranscribing(false);
      }
    },
    [
      transcrireAudio,
      addToCurrentInput,
      setIsTranscribing,
      setIsRecording,
      addTranscribedLength,
    ],
  );

  const {
    isRecording: isHookRecording,
    isSupported,
    startRecording,
    stopRecording,
  } = useAudioRecorder({
    onTranscript: handleTranscript,
    onError: (err) => {
      setAudioError(err);
      setIsRecording(false);
      setRecordingStartTime(null);
    },
    onMaxDurationReached: () => {
      setIsRecording(false);
      setRecordingStartTime(null);
      setAudioError(
        `Limite de ${maxDuration} minutes atteinte. L'enregistrement a été arrêté automatiquement.`,
      );
    },
    onSilenceDetected: () => {
      setIsRecording(false);
      setRecordingStartTime(null);
      setAudioError(
        `Silence détecté pendant ${silenceTimeout} secondes. L'enregistrement a été arrêté automatiquement.`,
      );
    },
    onVolumeChange: (volume) => {
      setAudioVolume(volume);
    },
  });

  // Sync hook recording state with store
  useEffect(() => {
    setIsRecording(isHookRecording);
  }, [isHookRecording, setIsRecording]);

  const handleMicClick = async () => {
    setAudioError(null);
    if (isRecording) {
      stopRecording();
      setRecordingStartTime(null);
    } else {
      const now = Date.now();
      if (firstResponseAtRef.current === null) {
        firstResponseAtRef.current = now;
      }
      setRecordingStartTime(now);
      await startRecording();
    }
  };

  // Déclenche la vocalisation TTS d'un message assistant
  const handleVocalize = useCallback(
    async (idx: number, text: string) => {
      const current = audioStates.get(idx);

      // Replay si l'audio est déjà chargé
      if (current?.blobUrl && current.audio) {
        current.audio.currentTime = 0;
        await current.audio.play();
        return;
      }

      // Chargement en cours
      setAudioStates((prev) => {
        const next = new Map(prev);
        next.set(idx, { status: "loading" });
        return next;
      });

      try {
        const { audioBase64, mimeType } = await vocaliserTexte({ text });

        // Décode base64 → Blob → URL
        const binaryStr = atob(audioBase64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        const audio = new Audio(blobUrl);

        audio.onended = () => {
          setAudioStates((prev) => {
            const next = new Map(prev);
            const state = next.get(idx);
            if (state) next.set(idx, { ...state, status: "ready" });
            return next;
          });
        };

        setAudioStates((prev) => {
          const next = new Map(prev);
          next.set(idx, { status: "playing", blobUrl, audio });
          return next;
        });

        await audio.play();
      } catch {
        setAudioStates((prev) => {
          const next = new Map(prev);
          next.set(idx, { status: "idle" });
          return next;
        });
      }
    },
    [audioStates, vocaliserTexte],
  );

  // Handle send message
  const handleSendMessage = async () => {
    if (!currentInput.trim() || isSubmitting) return;

    // Stop recording if active before sending
    if (isRecording) {
      stopRecording();
      return;
    }

    const messageToSend = currentInput.trim();
    const cheatingMeta = getCheatingMeta(messageToSend.length);
    const answeredAt = Date.now();
    const displayedAt = questionDisplayedAtRef.current ?? undefined;
    const typewriterEndAt = typewriterEndAtRef.current ?? undefined;
    const firstResponseAt = firstResponseAtRef.current ?? undefined;

    setCurrentInput("");
    setPendingMessage(messageToSend);
    setIsSubmitting(true);
    setIsTyping(true);

    const result = await envoyerMessage({
      evaluationId: evaluation._id,
      parcoursId: parcoursId as Id<"parcoursEvaluations">,
      token,
      message: messageToSend,
      caracteresTranscrits: transcribedLength,
      cheatingMeta,
      displayedAt,
      answeredAt,
      typewriterEndAt,
      firstResponseAt,
    });

    setPendingMessage(null);
    setIsTyping(false);
    setIsSubmitting(false);
    resetTranscribedLength();
    resetCheatingMeta();
    questionDisplayedAtRef.current = null;
    typewriterEndAtRef.current = null;
    firstResponseAtRef.current = null;

    if (!isBackendError(result)) {
      if (result.estTermine) {
        setIsInterviewFinished(true);
      }
    }
  };

  // Handle start interview (from intro modal)
  const handleStartInterview = async () => {
    setIsStartingInterview(true);
    try {
      await demarrerEvaluation({
        evaluationId: evaluation._id,
        parcoursId: parcoursId as Id<"parcoursEvaluations">,
        token,
      });

      // Si pas de messages (parcours créé via batch sans auto-init), initialiser maintenant
      if (messages.length === 0 && parcoursId) {
        const result = await initialiserEntretien({
          evaluationId: evaluation._id,
          parcoursId: parcoursId as Id<"parcoursEvaluations">,
          token,
        });
        if ("erreur" in result) {
          console.error("Erreur initialisation entretien:", result.erreur);
        }
      }

      setShowIntroModal(false);
    } catch (error) {
      console.error("Erreur lors du démarrage de l'entretien:", error);
    } finally {
      setIsStartingInterview(false);
    }
  };

  // Handle decline interview (redirect to candidate portal)
  const handleDeclineInterview = () => {
    const baseUrl = candidatePortailUrl ?? "";
    const finalParcoursId = parcoursIdFromStore || parcoursFromEval?.parcoursId;

    // If query is still loading and no store value, wait for it
    if (parcoursFromEval === undefined && !parcoursIdFromStore) {
      return;
    }

    if (finalParcoursId) {
      window.location.href = `${baseUrl}/p/${finalParcoursId}`;
    } else {
      // Fallback to base URL if no parcoursId available
      window.location.href = baseUrl;
    }
  };

  // Handle finish interview
  const handleFinishInterview = async () => {
    setIsSubmitting(true);
    await terminerEvaluation({
      evaluationId: evaluation._id,
      parcoursId: parcoursId as Id<"parcoursEvaluations">,
      token,
      donnees: evaluation.donnees,
    });
    setIsSubmitting(false);
    setShowCompletionModal(true);
  };

  const isMicBusy = isSubmitting || isTranscribing;

  return (
    <>
      <Card className="w-full max-w-4xl flex flex-col shadow-lg h-full sm:h-[85vh] rounded-none sm:rounded-lg py-1">
        {/* Header */}
        <div className="border-b px-4 py-1 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">
                Entretien Structuré
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-0.5 sm:mt-1">
                {isReadOnly
                  ? "Entretien terminé - Mode lecture seule"
                  : "Répondez aux questions du recruteur"}
              </p>
            </div>
            {!isReadOnly && progression.total > 0 && (
              <div className="flex flex-col items-end gap-1 min-w-24 sm:min-w-40">
                <span className="text-sm md:text-base font-medium">
                  {progression.percentage}%
                </span>
                <Progress
                  value={progression.percentage}
                  className="h-1.5 sm:h-2 w-full"
                />
              </div>
            )}
          </div>
        </div>

        {/* Messages area container - relative for button positioning */}
        <div className="relative flex-1 min-h-0">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="absolute inset-0 overflow-y-auto px-3 sm:px-6 space-y-4"
          >
            {/* Masquer les messages tant que la modale d'intro est ouverte */}
            {!showIntroModal &&
              messages.map((msg, idx) => (
                <ChatBubble
                  key={idx}
                  role={msg.role}
                  content={msg.content}
                  isLatest={idx === messages.length - 1 && !pendingMessage}
                  shouldTypeWrite={
                    !isReadOnly &&
                    idx === messages.length - 1 &&
                    msg.role === "assistant" &&
                    !isTyping &&
                    !pendingMessage
                  }
                  onTypewriterComplete={
                    idx === messages.length - 1 && msg.role === "assistant"
                      ? () => {
                          if (typewriterEndAtRef.current === null) {
                            typewriterEndAtRef.current = Date.now();
                          }
                        }
                      : undefined
                  }
                  onVocalize={
                    isTtsEnabled && msg.role === "assistant"
                      ? () => handleVocalize(idx, msg.content)
                      : undefined
                  }
                  audioStatus={
                    isTtsEnabled
                      ? (audioStates.get(idx)?.status ?? "idle")
                      : undefined
                  }
                />
              ))}
            {!showIntroModal && pendingMessage && (
              <ChatBubble
                role="user"
                content={pendingMessage}
                isLatest={true}
                shouldTypeWrite={false}
              />
            )}
            {!showIntroModal && isTyping && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {/* Scroll to bottom button - appears when user scrolls up */}
          {!isNearBottom && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-4 right-6 flex items-center gap-2 px-4 py-3 rounded-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-xl border border-gray-200 dark:border-gray-700 animate-in fade-in zoom-in duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 z-20 font-medium"
              title="Descendre vers les nouveaux messages"
            >
              <ChevronDown className="h-5 w-5" />
              {/* <span className="text-sm">Nouveaux messages</span> */}
            </button>
          )}
        </div>

        {/* Input area */}
        <div className="border-t px-3 pt-1 pb-0 sm:px-6 sm:py-4">
          {isReadOnly ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              L'entretien est terminé. Vous pouvez consulter vos réponses
              ci-dessus.
            </div>
          ) : isInterviewFinished ? (
            <Button
              onClick={handleFinishInterview}
              disabled={isSubmitting}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? "Finalisation..." : "Terminer l'entretien"}
            </Button>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <RecordingIndicator
                    isRecording={isRecording}
                    volume={audioVolume}
                    startTime={recordingStartTime}
                  />
                  <Textarea
                    ref={textareaRef}
                    value={currentInput}
                    onChange={(e) => {
                      const newValue = e.target.value || "";
                      const prevLength = currentInput.length;
                      const newLength = newValue.length;
                      if (newLength > prevLength) {
                        onKeystroke(newLength - prevLength);
                        if (firstResponseAtRef.current === null) {
                          firstResponseAtRef.current = Date.now();
                        }
                      }
                      setCurrentInput(newValue);
                    }}
                    onPaste={() => onPaste()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder={isMobile ? "Écrivez votre réponse ici..." : "Écrivez votre réponse ici... (Entrée pour envoyer, Shift+Entrée pour nouvelle ligne)"}
                    className={`min-h-24 sm:min-h-25 resize-none w-full ${isRecording ? "pt-8" : ""}`}
                    disabled={isSubmitting || isRecording || isTranscribing}
                  />
                  {isTranscribing && (
                    <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center gap-2 rounded-md z-10">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">
                        Transcription en cours...
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {isSupported && (
                    <Button
                      onClick={handleMicClick}
                      disabled={isMicBusy}
                      size="icon"
                      variant={isRecording ? "destructive" : "outline"}
                      className={`h-10 w-10 sm:h-12 sm:w-12 ${isRecording ? "animate-pulse" : ""}`}
                      title={
                        isRecording
                          ? "Arrêter l'enregistrement"
                          : "Dicter ma réponse"
                      }
                    >
                      {isTranscribing ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : isRecording ? (
                        <MicOff className="h-5 w-5" />
                      ) : (
                        <Mic className="h-5 w-5" />
                      )}
                    </Button>
                  )}
                  <Button
                    onClick={handleSendMessage}
                    disabled={
                      !currentInput.trim() ||
                      isSubmitting ||
                      isRecording ||
                      isTranscribing
                    }
                    size="icon"
                    className={`w-10 sm:w-12 ${isSupported ? "h-10 sm:h-11" : "h-16 sm:h-25"}`}
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              {audioError && (
                <p className="text-sm text-destructive">{audioError}</p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Intro Modal */}
      <IntroModal
        isOpen={showIntroModal}
        onConfirm={handleStartInterview}
        onDecline={handleDeclineInterview}
        isLoading={isStartingInterview}
      />

      {/* Completion Modal */}
      <CompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
      />
    </>
  );
}
